import { useState } from "react";
import { FaChevronDown, FaSpinner } from "react-icons/fa";
import { MascotLoadingOverlay } from "../../../shared";

// Bakım sekmesi ortak görsel parçaları — panelin genel kalıbıyla aynı:
// başlık şeritli beyaz kart (InfoTab / DeviceVersionsCard), bg-main ana buton
// (Ayarlar → Kaydet), kenarlıklı ikincil buton (Ayarlar → Yenile), iskelet +
// MascotLoadingOverlay yükleme kuralı (docs/rules/ui-components.md).

export const BTN_PRIMARY =
  "inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg bg-main text-white text-sm font-SemiBold shadow-sm hover:bg-mainSecond disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
export const BTN_SECONDARY =
  "inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-SemiBold shadow-sm hover:bg-gray-50 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
export const BTN_DANGER =
  "inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg border border-rose-300 bg-white text-rose-600 text-sm font-SemiBold shadow-sm hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
export const BTN_ICON =
  "inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors";

export const Spinner = ({ className = "w-3.5 h-3.5" }) => (
  <FaSpinner className={`${className} animate-spin`} aria-hidden="true" />
);

/** Başlık şeritli kart — sitedeki bilgi kartlarıyla aynı. */
export function SectionCard({
  icon: Icon,
  iconBg,
  title,
  right,
  children,
  className = "",
  testId,
}) {
  return (
    <section
      data-testid={testId}
      className={`rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden ${className}`}
    >
      <header className="flex flex-wrap items-center gap-2.5 px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
        <span className={`${iconBg} text-white p-1.5 rounded-lg shadow-sm`}>
          <Icon className="w-4 h-4" />
        </span>
        <h2 className="text-sm font-SemiBold text-gray-800">{title}</h2>
        {right && (
          <div className="ml-auto flex items-center gap-2">{right}</div>
        )}
      </header>
      <div className="p-4 flex flex-col gap-3">{children}</div>
    </section>
  );
}

/**
 * Sensör kutularının altındaki katlanabilir "Değerler nasıl okunur?" paneli.
 * @param {object} props
 * @param {string} props.testId
 * @param {React.ReactNode} [props.right] başlık sağı (ör. "Göz 2")
 */
export function ExplainerPanel({ testId, right, children }) {
  // Varsayılan açık: hassasiyet kontrolü de bu panelin içinde.
  const [open, setOpen] = useState(true);
  return (
    <aside
      data-testid={testId}
      className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 min-w-0"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 text-sm font-SemiBold text-gray-700 hover:text-gray-900"
        >
          <FaChevronDown
            className={`w-3 h-3 transition-transform ${open ? "" : "-rotate-90"}`}
            aria-hidden="true"
          />
          Değerler nasıl okunur?
        </button>
        {right}
      </div>
      {open && children}
    </aside>
  );
}

/** Küçük bölmeli seçici (göz / sensör seçimi). */
export function SegmentedTabs({ options, value, onChange, disabled }) {
  return (
    <div className="inline-flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={value === o.id}
          onClick={() => onChange(o.id)}
          disabled={disabled}
          className={`h-8 min-w-[2.25rem] px-2.5 rounded-md text-sm font-SemiBold transition-colors disabled:opacity-40 ${
            value === o.id
              ? "bg-main text-white shadow-sm"
              : "text-gray-600 hover:bg-white"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Canlı yayın rozeti: canlıyken atan yeşil nokta, beklerken dönen ikon. */
export function LiveBadge({ state }) {
  if (state === "live") {
    return (
      <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-xs font-SemiBold text-emerald-700">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        Canlı
      </span>
    );
  }
  if (state === "waiting") {
    return (
      <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg border border-amber-200 bg-amber-50 text-xs font-SemiBold text-amber-700">
        <Spinner className="w-3 h-3" />
        Bekleniyor
      </span>
    );
  }
  return (
    <span className="inline-flex items-center h-7 px-2.5 rounded-lg border border-gray-200 bg-gray-50 text-xs font-SemiBold text-gray-500">
      Kapalı
    </span>
  );
}

/** Kart içi bilgi satırı (ikon + başlık + açıklama). */
export function Notice({ icon: Icon, tone = "gray", title, children }) {
  const tones = {
    gray: "border-gray-200 bg-gray-50 text-gray-700",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
  };
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${tones[tone]}`}
    >
      {Icon && <Icon className="w-4 h-4 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <div className="text-sm font-SemiBold">{title}</div>
        {children && (
          <div className="text-xs opacity-80 mt-0.5">{children}</div>
        )}
      </div>
    </div>
  );
}

const Bone = ({ className }) => (
  <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
);

/** Kart ızgarası iskeleti (göz / demlik kartları). */
export function CardGridSkeleton({ count = 4 }) {
  return (
    <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(230px,1fr))]">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-gray-200 bg-white p-3 space-y-3"
        >
          <div className="flex items-center gap-2">
            <Bone className="w-11 h-11 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Bone className="h-3 w-12" />
              <Bone className="h-4 w-24" />
            </div>
          </div>
          <Bone className="h-1.5 w-full" />
        </div>
      ))}
    </div>
  );
}

/** Değer satırları iskeleti (askı sensörü canlı değerleri). */
export function ValueRowsSkeleton({ rows = 4 }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2.5"
        >
          <div className="space-y-1.5">
            <Bone className="h-3.5 w-28" />
            <Bone className="h-3 w-44" />
          </div>
          <Bone className="h-5 w-12" />
        </div>
      ))}
    </div>
  );
}

/**
 * Sekmenin ilk yüklemesi — iskelet üstünde maskot (site kuralı:
 * MascotLoadingOverlay her zaman iskeletin üstünde).
 */
export function MaintenanceSkeleton({ message, cardCount = 4, withHanger }) {
  return (
    <div className="relative flex flex-col gap-4">
      <MascotLoadingOverlay message={message} />
      <div className="opacity-40 pointer-events-none flex flex-col gap-4">
        <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-2.5 px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
            <Bone className="w-7 h-7 rounded-lg" />
            <Bone className="h-4 w-28" />
            <Bone className="ml-auto h-9 w-24 rounded-lg" />
          </div>
          <div className="p-4">
            <CardGridSkeleton count={cardCount} />
          </div>
        </div>
        {withHanger && (
          <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-2.5 px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
              <Bone className="w-7 h-7 rounded-lg" />
              <Bone className="h-4 w-28" />
            </div>
            <div className="p-4">
              <ValueRowsSkeleton rows={2} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
